/**
 * Modern Agent Dashboard
 * 
 * A comprehensive dashboard for agents featuring:
 * - Real-time performance metrics
 * - Quick access to conversations
 * - Status management with analytics
 * - Workload visualization
 * - Today's activity summary
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Select } from '../../components/ui/Select'
import { 
  Users, 
  MessageSquare, 
  Clock, 
  Star, 
  Settings,
  LogOut,
  RefreshCw,
  Circle,
  Award,
  TrendingUp,
  Calendar,
  Bell,
  ChevronRight,
  Activity,
  Target,
  Timer,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Eye,
  ArrowUpRight
} from 'lucide-react'

import { api } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { logger } from '../../utils/logger'
import { formatDuration } from '../../utils/time'

interface AgentProfile {
  id: string
  email: string
  full_name: string
  user_type: string
  agent_id: string
  agent_name: string
  agent_role: string
  team_id: string
  team_name: string
  is_active: boolean
  created_at: string
  // Agent status fields from backend
  status: string  // 'online', 'away', 'busy', 'offline'
  is_online: boolean
  last_seen?: string
  status_message?: string
  current_chat_count: number
  max_concurrent_chats: number
}

interface AgentStats {
  active_conversations: number
  pending_handoffs: number
  total_conversations_today: number
  avg_response_time: number
  customer_satisfaction: number
  workload_percentage: number
  conversations_completed_today: number
  total_messages_today: number
  online_time_minutes: number
}

export const AgentDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { logout, user, isAuthenticated, isLoading: authLoading } = useAuth()
  
  const [profile, setProfile] = useState<AgentProfile | null>(null)
  const [stats, setStats] = useState<AgentStats | null>(null)
  const [currentStatus, setCurrentStatus] = useState('offline')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAnalyticsModalOpen, setAnalyticsModalOpen] = useState(false)
  
  useEffect(() => {
    logger.debug('🚀 AgentDashboard mounted', {
      component: 'AgentDashboard',
      isAuthenticated,
      authLoading,
      user: user ? { id: user.id, email: user.email } : null,
      localStorage: {
        token: localStorage.getItem('token') ? 'present' : 'missing',
        user_type: localStorage.getItem('user_type')
      }
    })
    
    if (isAuthenticated && !authLoading) {
      logger.debug('🔄 Starting data fetch', { component: 'AgentDashboard' })
      fetchAgentProfile()
      fetchAgentStats()
    }
  }, [isAuthenticated, authLoading])
  
  const fetchAgentProfile = async () => {
    try {
      logger.debug('📞 Fetching agent profile', {
        component: 'AgentDashboard',
        endpoint: '/agents/auth/profile'
      })
      
      const response = await api.get('/agents/auth/profile')
      
      logger.debug('✅ Agent profile response received', {
        component: 'AgentDashboard',
        data: response.data,
        status: response.status
      })
      
      setProfile(response.data)
      
      // Set current status from profile response
      setCurrentStatus(response.data.status || 'offline')
      
      logger.info('Agent profile loaded successfully', {
        component: 'AgentDashboard',
        agentId: response.data.agent_id,
        email: response.data.email,
        status: response.data.status
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setError('Failed to load profile')
      
      logger.error('❌ Failed to fetch agent profile', {
        component: 'AgentDashboard',
        error: errorMessage,
        status: (error as any)?.response?.status,
        data: (error as any)?.response?.data
      })
    }
  }
  
  const fetchAgentStats = async () => {
    try {
      logger.debug('📊 Fetching real agent statistics', {
        component: 'AgentDashboard',
        endpoint: '/agents/auth/stats'
      })
      
      const response = await api.get('/agents/auth/stats')
      
      logger.debug('✅ Agent statistics response received', {
        component: 'AgentDashboard',
        data: response.data,
        status: response.status
      })
      
      setStats(response.data)
      setIsLoading(false)
      
      logger.info('Agent statistics loaded successfully', {
        component: 'AgentDashboard',
        activeConversations: response.data.active_conversations,
        pendingHandoffs: response.data.pending_handoffs,
        todayConversations: response.data.total_conversations_today
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setError('Failed to load statistics')
      setIsLoading(false)
      
      logger.error('❌ Failed to fetch agent statistics', {
        component: 'AgentDashboard',
        error: errorMessage,
        status: (error as any)?.response?.status,
        data: (error as any)?.response?.data
      })
    }
  }
  
  const handleStatusChange = async (newStatus: string) => {
    try {
      if (!profile) return
      
      logger.debug('📞 Updating agent status', {
        component: 'AgentDashboard',
        oldStatus: currentStatus,
        newStatus: newStatus
      })
      
      await api.put('/agents/auth/status', {
        status: newStatus
      })
      
      setCurrentStatus(newStatus)
      
      logger.info('✅ Agent status updated successfully', {
        component: 'AgentDashboard',
        agentId: profile.agent_id,
        status: newStatus
      })
    } catch (error) {
      logger.error('❌ Failed to update status', {
        component: 'AgentDashboard',
        error: error instanceof Error ? error.message : 'Unknown error',
        status: (error as any)?.response?.status,
        data: (error as any)?.response?.data
      })
    }
  }
  
  const handleLogout = async () => {
    try {
      await api.post('/agents/auth/logout')
      logout()
      navigate('/agent/login')
    } catch (error) {
      // Logout anyway even if API call fails
      logout()
      navigate('/agent/login')
    }
  }
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500'
      case 'away': return 'bg-yellow-500'
      case 'busy': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }
  
  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1)
  }
  
  if (authLoading || isLoading) {
    logger.debug('📱 Showing loading state', {
      component: 'AgentDashboard',
      authLoading,
      isLoading,
      isAuthenticated
    })
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading agent dashboard...</p>
          <p className="text-xs text-gray-400 mt-2">
            Auth: {authLoading ? 'loading' : 'ready'}, Data: {isLoading ? 'loading' : 'ready'}
          </p>
        </div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    logger.debug('🚫 Not authenticated, redirecting to login', {
      component: 'AgentDashboard'
    })
    
    navigate('/agent/login')
    return null
  }
  
  if (error || !profile) {
    logger.debug('❌ Showing error state', {
      component: 'AgentDashboard',
      error,
      hasProfile: !!profile
    })
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">{error || 'Failed to load dashboard'}</p>
            <p className="text-xs text-gray-500 mb-4">
              Auth: {isAuthenticated ? 'yes' : 'no'}, Profile: {profile ? 'loaded' : 'missing'}
            </p>
            <Button onClick={() => {
              // Retry without page reload - pure real-time approach
              fetchAgentProfile()
              fetchAgentStats()
            }}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome back, {profile.agent_name}!</h1>
              <p className="text-gray-600 mt-1">
                You're logged in as <span className="font-medium">{profile.agent_role}</span> on the {profile.team_name} team
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Status Selector */}
              <div className="flex items-center space-x-2">
                <Circle className={`w-3 h-3 ${getStatusColor(currentStatus)}`} />
                <Select
                  value={currentStatus}
                  onChange={handleStatusChange}
                  options={[
                    { value: 'online', label: 'Online' },
                    { value: 'away', label: 'Away' },
                    { value: 'busy', label: 'Busy' },
                    { value: 'offline', label: 'Offline' }
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 p-6 overflow-auto">
        
        
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/agent/conversations')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-gray-900">View Conversations</p>
                  <p className="text-sm text-gray-600">Manage and join customer chats</p>
                </div>
                <div className="flex items-center text-blue-600">
                  <MessageSquare className="w-6 h-6 mr-2" />
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setAnalyticsModalOpen(true)}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-gray-900">Analytics</p>
                  <p className="text-sm text-gray-600">View detailed performance metrics</p>
                </div>
                <div className="flex items-center text-green-600">
                  <BarChart3 className="w-6 h-6 mr-2" />
                  <span className="text-sm">View</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-gray-900">Settings</p>
                  <p className="text-sm text-gray-600">Customize your preferences</p>
                </div>
                <div className="flex items-center text-gray-600">
                  <Settings className="w-6 h-6 mr-2" />
                  <span className="text-sm">Coming Soon</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Quick View Modal */}
        <Modal
          isOpen={isAnalyticsModalOpen}
          onClose={() => setAnalyticsModalOpen(false)}
          title="Agent Analytics"
          description="Today’s key performance metrics"
          size="lg"
        >
          {stats ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-900">{formatDuration(stats.avg_response_time)}</div>
                  <div className="text-sm text-gray-600">Avg Response</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.total_messages_today}</div>
                  <div className="text-sm text-gray-600">Messages Today</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.total_conversations_today}</div>
                  <div className="text-sm text-gray-600">Accepted Today</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.conversations_completed_today}</div>
                  <div className="text-sm text-gray-600">Completed Today</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.pending_handoffs}</div>
                  <div className="text-sm text-gray-600">Pending Handoffs</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.active_conversations}</div>
                  <div className="text-sm text-gray-600">Active Chats</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white border rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Reactions (7 days)</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-green-600">👍 {stats.positive_reactions}</span>
                      <span className="flex items-center gap-1 text-red-600">👎 {stats.negative_reactions}</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white border rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Current Workload</span>
                    <span className="font-semibold">{Math.round(stats.workload_percentage)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className={`h-2 rounded-full ${
                        stats.workload_percentage > 80 ? 'bg-red-500' :
                        stats.workload_percentage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`} style={{ width: `${Math.min(stats.workload_percentage, 100)}%` }} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-gray-600">No data available</div>
          )}
        </Modal>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Performance Analytics */}
          <div className="space-y-6">
            {/* Today's Performance */}
            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Activity className="w-5 h-5 mr-2" />
                    Today's Performance
                  </CardTitle>
                  <CardDescription>
                    Your activity and performance metrics for today
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Performance Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{stats.total_conversations_today}</div>
                      <div className="text-sm text-gray-600">Total Today</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {stats.conversations_completed_today} completed
                      </div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{formatDuration(stats.avg_response_time)}</div>
                      <div className="text-sm text-gray-600">Avg Response</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {stats.total_messages_today} messages sent
                      </div>
                    </div>
                  </div>
                  
                  {/* Reactions Summary */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Reactions (7 days)</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-green-600">👍 {stats.positive_reactions}</span>
                        <span className="flex items-center gap-1 text-red-600">👎 {stats.negative_reactions}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Current Workload */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Current Workload</span>
                      <span className="font-bold text-lg">{stats.workload_percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          stats.workload_percentage > 80 ? 'bg-red-500' :
                          stats.workload_percentage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${stats.workload_percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500">
                      {stats.active_conversations} of {profile.max_concurrent_chats} max concurrent chats
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Status & Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  Status & Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Circle className={`w-3 h-3 mr-3 ${getStatusColor(currentStatus)}`} />
                    <div>
                      <p className="font-medium">Current Status</p>
                      <p className="text-sm text-gray-600">{getStatusLabel(currentStatus)}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={
                    currentStatus === 'online' ? 'border-green-200 text-green-800' :
                    currentStatus === 'away' ? 'border-yellow-200 text-yellow-800' :
                    currentStatus === 'busy' ? 'border-red-200 text-red-800' :
                    'border-gray-200 text-gray-800'
                  }>
                    {getStatusLabel(currentStatus)}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{stats?.active_conversations || 0}</div>
                    <div className="text-xs text-gray-600">Active Chats</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{stats?.pending_handoffs || 0}</div>
                    <div className="text-xs text-gray-600">Pending</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Agent Profile & Team Info */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Your Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                    {profile.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{profile.full_name}</p>
                    <p className="text-sm text-gray-600">{profile.email}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Role</p>
                    <Badge variant="outline" className="mt-1">{profile.agent_role}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Team</p>
                    <p className="font-medium mt-1">{profile.team_name}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Member Since</p>
                  <p className="font-medium">{new Date(profile.created_at).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Timer className="w-5 h-5 mr-2" />
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm">Conversations Completed</span>
                    </div>
                    <span className="font-semibold">{stats?.conversations_completed_today || 0}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <AlertTriangle className="w-4 h-4 text-orange-500 mr-2" />
                      <span className="text-sm">Pending Handoffs</span>
                    </div>
                    <span className="font-semibold">{stats?.pending_handoffs || 0}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-blue-500 mr-2" />
                      <span className="text-sm">Online Time Today</span>
                    </div>
                    <span className="font-semibold">{Math.round((stats?.online_time_minutes || 0) / 60 * 10) / 10}h</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  )
}

export default AgentDashboard
